import React, { useCallback, useEffect, useState } from "react";
import {
  Activity,
  CheckCircle2,
  CircleOff,
  Clock3,
  DatabaseZap,
  Loader2,
  Play,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  TriangleAlert,
  Wifi,
} from "lucide-react";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { SERVICE_OPTIONS } from "@/lib/serviceCatalog";

type MetaForm = {
  id: number;
  page_id: string;
  page_name: string | null;
  form_id: string;
  form_name: string;
  form_status: string | null;
  form_locale: string | null;
  default_service: string;
  service_field_key: string | null;
  default_timeframe: string;
  campaign_label: string | null;
  is_active: boolean;
  meta_created_at: string | null;
  last_synced_at: string | null;
  updated_at: string;
};

type MetaEvent = {
  id: number;
  form_id: string;
  form_name: string | null;
  status: string;
  attempts: number;
  received_count: number;
  last_error_code: string | null;
  received_at: string;
  processed_at: string | null;
  lead_id: string | null;
};

type MetaCrmEvent = {
  id: number;
  lead_stage: string;
  event_name: string;
  status: string;
  attempts: number;
  max_attempts: number;
  last_error_code: string | null;
  http_code: number | null;
  event_time: string;
  sent_at: string | null;
  created_at: string;
};

type Snapshot = {
  connection: {
    configured: boolean;
    graph_version: string | null;
    pages: Array<{ id: string }>;
  };
  crm_feedback: {
    configured: boolean;
    graph_version: string;
    test_mode: boolean;
    counts: Record<string, number>;
    recent_events: MetaCrmEvent[];
  };
  counts: Record<string, number>;
  forms: MetaForm[];
  recent_events: MetaEvent[];
};

const SERVICES = SERVICE_OPTIONS.map(({ value, label }) => [value, label] as const);

const TIMEFRAMES = [
  ["κάνωΈρευνα", "Κάνω έρευνα αγοράς"],
  ["άμεσα", "Άμεσα"],
  ["εντός3μηνών", "Εντός 3 μηνών"],
] as const;

const statusMeta: Record<string, { label: string; className: string }> = {
  processed: { label: "Ολοκληρώθηκε", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "Σε αναμονή", className: "bg-blue-50 text-blue-700 border-blue-200" },
  processing: { label: "Επεξεργασία", className: "bg-violet-50 text-violet-700 border-violet-200" },
  retry: { label: "Επανάληψη", className: "bg-amber-50 text-amber-700 border-amber-200" },
  unmapped: { label: "Χωρίς mapping", className: "bg-slate-50 text-slate-700 border-slate-200" },
  failed: { label: "Απέτυχε", className: "bg-red-50 text-red-700 border-red-200" },
  sent: { label: "Στάλθηκε", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const crmStageLabels: Record<string, string> = {
  Νέο: "Νέο lead",
  σεΕκκρεμότητα: "Έγινε επικοινωνία",
  ήθελεΠληροφορίες: "Ποιοτικό lead",
  έκλεισεΗΔουλειά: "Έκλεισε η δουλειά",
};

function apiUrl(): string {
  const configured = String(import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");
  return `${configured}/meta/admin.php`;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value.includes("T") ? value : `${value.replace(" ", "T")}Z`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("el-GR");
}

export default function MetaLeadHub() {
  const { toast } = useToast();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const request = useCallback(async (body?: Record<string, unknown>) => {
    const response = await fetch(apiUrl(), {
      method: body ? "POST" : "GET",
      credentials: "include",
      headers: {
        ...(body ? { "Content-Type": "application/json" } : {}),
        "X-CSRF-Token": sessionStorage.getItem("csrf_token") || "",
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.message || "Η ενέργεια απέτυχε");
    }
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request();
      setSnapshot(data as Snapshot);
    } catch (error) {
      toast({
        title: "Δεν φορτώθηκε το Meta Lead Hub",
        description: error instanceof Error ? error.message : "Άγνωστο σφάλμα",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [request, toast]);

  useEffect(() => { void load(); }, [load]);

  const runAction = async (action: string, payload: Record<string, unknown> = {}) => {
    setBusy(action);
    try {
      const data = await request({ action, ...payload });
      if (data.snapshot) setSnapshot(data.snapshot);
      toast({
        title: action === "sync_forms" ? "Οι φόρμες συγχρονίστηκαν" : "Η ενέργεια ολοκληρώθηκε",
        description: action === "process_pending"
          ? `${data.result?.processed ?? 0} events επεξεργάστηκαν.`
          : action === "process_crm_feedback"
            ? `${data.result?.sent ?? 0} ενημερώσεις στάλθηκαν στη Meta.`
          : undefined,
      });
    } catch (error) {
      toast({
        title: "Η ενέργεια απέτυχε",
        description: error instanceof Error ? error.message : "Άγνωστο σφάλμα",
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  };

  const updateForm = (formId: string, patch: Partial<MetaForm>) => {
    setSnapshot(current => current ? {
      ...current,
      forms: current.forms.map(form => form.form_id === formId ? { ...form, ...patch } : form),
    } : current);
  };

  const saveForm = async (form: MetaForm) => {
    await runAction(`save-${form.form_id}`, {
      action: "save_form",
      form_id: form.form_id,
      default_service: form.default_service,
      service_field_key: form.service_field_key || "",
      default_timeframe: form.default_timeframe,
      campaign_label: form.campaign_label || "",
      is_active: form.is_active,
    });
  };

  if (loading && !snapshot) {
    return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  const statCards: Array<{ label: string; value: React.ReactNode; icon: React.ElementType }> = [
    { label: "Σύνδεση", value: snapshot?.connection.configured ? "Έτοιμη" : "Εκκρεμεί", icon: snapshot?.connection.configured ? CheckCircle2 : TriangleAlert },
    { label: "Φόρμες", value: snapshot?.counts.forms ?? 0, icon: Activity },
    { label: "Ενεργές", value: snapshot?.counts.active_forms ?? 0, icon: CheckCircle2 },
    { label: "Ανενεργές", value: snapshot?.counts.inactive_forms ?? 0, icon: CircleOff },
    { label: "Queue", value: (snapshot?.counts.pending ?? 0) + (snapshot?.counts.retry ?? 0), icon: Clock3 },
  ];
  return (
    <div className="space-y-6 pb-10">
      <PageHeader
        title="Meta Lead Hub"
        description="Realtime φόρμες Meta με idempotent intake, ανάθεση και ασφαλή ουρά ειδοποιήσεων"
        icon={<DatabaseZap />}
        iconGradient="violet"
        actions={(
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => runAction("test_connection")} disabled={busy !== null}>
              {busy === "test_connection" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wifi className="h-4 w-4 mr-2" />}
              Έλεγχος σύνδεσης
            </Button>
            <Button onClick={() => runAction("sync_forms")} disabled={busy !== null || !snapshot?.connection.configured}>
              {busy === "sync_forms" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Συγχρονισμός φορμών
            </Button>
          </div>
        )}
      />

      <Card className="border-blue-200 bg-blue-50/60">
        <CardContent className="pt-5 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-blue-950">Ασφαλής παράλληλη λειτουργία</p>
            <p className="text-sm text-blue-800 mt-1">
              Η κύρια ροή είναι πλέον Meta API → Lead Hub → CRM. Το dormant Sheets endpoint παραμένει μόνο ως κώδικας rollback, χωρίς ενεργό trigger. Το idempotency key εξασφαλίζει μία επαφή και μία ειδοποίηση ανά κανάλι.
              Νέες φόρμες καταγράφονται αυτόματα αλλά δεν αντλούν προσωπικά στοιχεία μέχρι να ενεργοποιηθούν εδώ.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {statCards.map(({ label, value, icon: Icon }) => (
          <Card key={label} className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between text-slate-500">
                <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
                <Icon className="h-4 w-4" />
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-2">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {!snapshot?.connection.configured && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-5 flex gap-3">
            <TriangleAlert className="h-5 w-5 text-amber-700 shrink-0" />
            <div>
              <p className="font-semibold text-amber-950">Το realtime κανάλι δεν έχει ενεργοποιηθεί ακόμη</p>
              <p className="text-sm text-amber-800 mt-1">
                Χρειάζεται απομονωμένο Page token για τη Διαθερμική και HMAC secret στο private directory του server.
                Κανένα secret δεν αποθηκεύεται από αυτή την οθόνη ή μέσα στο repository.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className={snapshot?.crm_feedback.configured ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/60"}>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex flex-wrap items-center gap-2">
              Αποτελέσματα leads προς Meta
              <Badge variant="outline" className={snapshot?.crm_feedback.configured ? "border-emerald-200 bg-white text-emerald-700" : "border-amber-200 bg-white text-amber-700"}>
                {snapshot?.crm_feedback.configured ? "Συνδεδεμένο" : "Δεν έχει συνδεθεί"}
              </Badge>
              {snapshot?.crm_feedback.test_mode && <Badge variant="secondary">Test mode</Badge>}
            </CardTitle>
            <CardDescription className="mt-1 max-w-3xl">
              Όταν η ομάδα ενημερώνει την πορεία ενός Meta lead, το CRM επιστρέφει το αποτέλεσμα στη Meta. Έτσι οι καμπάνιες μπορούν να βελτιστοποιούνται για πραγματικά ποιοτικά leads και όχι μόνο για συμπληρωμένες φόρμες.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => runAction("process_crm_feedback")} disabled={busy !== null || !snapshot?.crm_feedback.configured}>
            {busy === "process_crm_feedback" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Αποστολή τώρα
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Σε αναμονή", (snapshot?.crm_feedback.counts.pending ?? 0) + (snapshot?.crm_feedback.counts.retry ?? 0)],
              ["Στάλθηκαν", snapshot?.crm_feedback.counts.sent ?? 0],
              ["Σε επεξεργασία", snapshot?.crm_feedback.counts.processing ?? 0],
              ["Χρειάζονται έλεγχο", snapshot?.crm_feedback.counts.failed ?? 0],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-xl border border-white/80 bg-white/80 p-3 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
                <p className="mt-1 text-xl font-bold text-slate-900">{value}</p>
              </div>
            ))}
          </div>

          {!snapshot?.crm_feedback.configured && (
            <p className="text-sm text-amber-900">
              Το CRM δεν θα στείλει δεδομένα μέχρι να επιβεβαιωθούν το Dataset ID και το server token. Οι αλλαγές σταδίων παραμένουν ασφαλείς μέσα στο CRM.
            </p>
          )}

          <div className="overflow-x-auto rounded-xl border border-white/80 bg-white/80">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-slate-500">
                <th className="px-4 py-2 font-medium">Ώρα</th>
                <th className="px-4 py-2 font-medium">Αποτέλεσμα CRM</th>
                <th className="px-4 py-2 font-medium">Κατάσταση</th>
                <th className="px-4 py-2 font-medium">Προσπάθειες</th>
                <th className="px-4 py-2 font-medium">Έλεγχος</th>
              </tr></thead>
              <tbody>
                {snapshot?.crm_feedback.recent_events.map(event => {
                  const meta = statusMeta[event.status] || { label: event.status, className: "bg-slate-50 text-slate-700 border-slate-200" };
                  return (
                    <tr key={event.id} className="border-b last:border-0">
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(event.event_time)}</td>
                      <td className="px-4 py-3">{crmStageLabels[event.lead_stage] || event.lead_stage}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className={meta.className}>{meta.label}</Badge></td>
                      <td className="px-4 py-3">{event.attempts}/{event.max_attempts}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{event.last_error_code || (event.http_code ? `HTTP ${event.http_code}` : "—")}</td>
                    </tr>
                  );
                })}
                {snapshot?.crm_feedback.recent_events.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Δεν υπάρχουν ακόμη ενημερώσεις CRM προς Meta.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Meta φόρμες</CardTitle>
            <CardDescription>Mapping υπηρεσίας και ρητή ενεργοποίηση ανά φόρμα</CardDescription>
          </div>
          <Badge variant="outline">Graph {snapshot?.connection.graph_version || "—"}</Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          {snapshot?.forms.length === 0 && (
            <div className="text-center py-10 text-slate-500">
              Δεν έχουν συγχρονιστεί φόρμες ακόμη.
            </div>
          )}
          {snapshot?.forms.map(form => (
            <div key={form.form_id} className="rounded-xl border border-slate-200 p-4 lg:p-5 space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{form.form_name}</h3>
                    <Badge variant="outline" className={form.is_active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-600"}>
                      {form.is_active ? "Ενεργή" : "Ανενεργή"}
                    </Badge>
                    {form.form_status && <Badge variant="secondary">Meta: {form.form_status}</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Form ID {form.form_id} · {form.page_name || `Page ${form.page_id}`} · Τελευταίο sync {formatDate(form.last_synced_at)}
                  </p>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
                  <Label htmlFor={`active-${form.form_id}`} className="text-sm">Realtime λήψη</Label>
                  <Switch
                    id={`active-${form.form_id}`}
                    checked={form.is_active}
                    onCheckedChange={checked => updateForm(form.form_id, { is_active: checked })}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Προεπιλεγμένη υπηρεσία</Label>
                  <Select value={form.default_service} onValueChange={value => updateForm(form.form_id, { default_service: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SERVICES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Πεδίο επιλογής υπηρεσίας</Label>
                  <Input
                    value={form.service_field_key || ""}
                    onChange={event => updateForm(form.form_id, { service_field_key: event.target.value })}
                    placeholder="π.χ. ποιο_προϊόν_σας_ενδιαφέρει;"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Χρονικός ορίζοντας</Label>
                  <Select value={form.default_timeframe} onValueChange={value => updateForm(form.form_id, { default_timeframe: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEFRAMES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ετικέτα καμπάνιας</Label>
                  <Input
                    value={form.campaign_label || ""}
                    onChange={event => updateForm(form.form_id, { campaign_label: event.target.value })}
                    placeholder="Προαιρετική εσωτερική ονομασία"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => saveForm(form)} disabled={busy !== null}>
                  {busy === `save-${form.form_id}` ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Αποθήκευση mapping
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Realtime queue</CardTitle>
            <CardDescription>Τελευταία events χωρίς εμφάνιση προσωπικών δεδομένων</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => runAction("process_pending")} disabled={busy !== null}>
            {busy === "process_pending" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Επεξεργασία queue
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-slate-500">
                <th className="py-2 pr-4 font-medium">Ώρα</th>
                <th className="py-2 pr-4 font-medium">Φόρμα</th>
                <th className="py-2 pr-4 font-medium">Κατάσταση</th>
                <th className="py-2 pr-4 font-medium">Παραλαβές</th>
                <th className="py-2 pr-4 font-medium">Σφάλμα</th>
                <th className="py-2 font-medium text-right">Ενέργεια</th>
              </tr></thead>
              <tbody>
                {snapshot?.recent_events.map(event => {
                  const meta = statusMeta[event.status] || { label: event.status, className: "bg-slate-50 text-slate-700 border-slate-200" };
                  return (
                    <tr key={event.id} className="border-b last:border-0">
                      <td className="py-3 pr-4 whitespace-nowrap">{formatDate(event.received_at)}</td>
                      <td className="py-3 pr-4 max-w-[280px] truncate" title={event.form_name || event.form_id}>{event.form_name || event.form_id}</td>
                      <td className="py-3 pr-4"><Badge variant="outline" className={meta.className}>{meta.label}</Badge></td>
                      <td className="py-3 pr-4">{event.received_count}</td>
                      <td className="py-3 pr-4 text-xs text-slate-500">{event.last_error_code || "—"}</td>
                      <td className="py-3 text-right">
                        {event.status !== "processed" && event.status !== "unmapped" && (
                          <Button variant="ghost" size="sm" onClick={() => runAction(`retry-${event.id}`, { action: "retry_event", event_id: event.id })} disabled={busy !== null}>
                            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {snapshot?.recent_events.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-slate-500">Δεν υπάρχουν realtime events ακόμη.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
